import { useMemo, type JSX, type ReactNode } from 'react';
import { SectionHeader } from '@/components/blocks/page';
import { Section } from '@/components/blocks/layout';
import {
  ActivityBar,
  ActivityTile,
  Panel,
  ReportTable,
  TableFooterLink,
  type ReportTableRow,
  type ActivityTileProps,
} from '@/components/blocks/data-display';
import { EmptyState } from '@/components/blocks/feedback';
import type { ReportSummary } from '@/data/repos/reportsRepo';
import type { Company } from '@/data/schemas';
import { formatDate } from '../companyDetailDates';
import { formatRelativeDate } from '@/utils/date';
import { CompanyAnagrafica } from '../../../components/CompanyAnagrafica';

export type ActivityItem = Pick<
  ActivityTileProps,
  'title' | 'subtitle' | 'icon' | 'actionLabel' | 'onClick'
> & {
  id: string;
};

/** @deprecated Use ActivityItem instead */
export type ActionItem = ActivityItem;

export function CompanyOverviewTab({
  actionItems,
  onCreateReport,
  reports,
  siteNameMap,
  hasSites,
  onReportsClick,
  onOpenReport,
  onEditReport,
  children,
}: {
  actionItems: ActionItem[];
  onCreateReport: () => void;
  reports: ReportSummary[];
  siteNameMap?: Map<string, string>;
  hasSites: boolean;
  onReportsClick: () => void;
  onOpenReport: (reportId: string) => void;
  onEditReport: (reportId: string) => void;
  children?: ReactNode;
}): JSX.Element {
  const reportRows = useMemo<ReportTableRow[]>(() => {
    return reports.map((report) => {
      const visitLabel = report.visitDate ? formatDate(report.visitDate) : '—';
      const reportCode = report.title || '—';
      const resolvedSiteName = siteNameMap?.get(report.siteId) ?? report.siteName;

      const lastUpdatedLabel = report.updatedAt
        ? formatRelativeDate(report.updatedAt)
        : report.finalizedAt
          ? formatRelativeDate(report.finalizedAt)
          : '—';

      return {
        id: report.id,
        dateLabel: visitLabel,
        companyName: report.companyName,
        siteName: resolvedSiteName,
        reportCode,
        ncOpen: report.nonConformitiesOpen ?? 0,
        lastUpdatedLabel,
        status: report.status,
      };
    });
  }, [reports, siteNameMap]);

  return (
    <Section spacing="lg">
      <Section>
        <SectionHeader title="Attività" />
        <ActivityBar emptyMessage="Nessuna attività prioritaria per questa azienda.">
          {actionItems.map((item) => (
            <ActivityTile
              key={item.id}
              title={item.title}
              subtitle={item.subtitle}
              icon={item.icon}
              actionLabel={item.actionLabel}
              onClick={item.onClick}
            />
          ))}
        </ActivityBar>
      </Section>

      {children}

      <Section>
        <SectionHeader
          title="Report recenti"
          action={{
            label: 'Nuovo report',
            onClick: onCreateReport,
          }}
        />
        {!hasSites ? (
          <Panel>
            <EmptyState
              icon="assignment_turned_in"
              title="Nessun report disponibile"
              description="Aggiungi uno stabilimento per creare e visualizzare i report."
              variant="inline"
            />
          </Panel>
        ) : reportRows.length > 0 ? (
          <Panel
            contentClassName="p-0"
            className="overflow-hidden"
            footerClassName="p-0"
            footer={<TableFooterLink label="Tutti i report" onClick={onReportsClick} />}
          >
            <ReportTable
              rows={reportRows}
              showCompany={false}
              showSite
              onRowClick={(row) =>
                row.status === 'draft' ? onEditReport(row.id) : onOpenReport(row.id)
              }
              emptyMessage="Nessun report disponibile"
              density="compact"
            />
          </Panel>
        ) : (
          <Panel>
            <EmptyState
              icon="assignment_turned_in"
              title="Nessun report disponibile"
              description="Crea un report per iniziare."
              variant="inline"
            />
          </Panel>
        )}
      </Section>
    </Section>
  );
}

export function CompanyRegistrySection({
  company,
  onEditCompany,
}: {
  company: Company | null | undefined;
  onEditCompany?: () => void;
}): JSX.Element {
  return (
    <Section>
      <SectionHeader
        title="Anagrafica azienda"
        action={
          onEditCompany
            ? {
                label: 'Modifica',
                onClick: onEditCompany,
              }
            : undefined
        }
      />
      <CompanyAnagrafica company={company} />
    </Section>
  );
}
